import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Icon from '../../../components/AppIcon';

const BookChapterModal = ({ 
  isOpen, 
  onClose, 
  books = [], 
  selectedBook,
  selectedChapter,
  onNavigate,
  defaultView = 'book'
}) => {
  const [view, setView] = useState(defaultView);
  const [activeTestament, setActiveTestament] = useState('old');
  const [currentBook, setCurrentBook] = useState(selectedBook);

  useEffect(() => {
    if (isOpen) {
      setCurrentBook(selectedBook);
      const bookData = books?.find(b => b?.name === selectedBook);
      if (bookData) {
        setActiveTestament(bookData?.testament || 'old');
      }
    } else {
      setView(defaultView);
    }
  }, [isOpen, defaultView, selectedBook, books]);

  const handleBookSelect = (bookName) => {
    setCurrentBook(bookName);
    setView('chapter');
  };

  const handleChapterSelect = (chapterNumber) => {
    onNavigate(currentBook, chapterNumber);
    onClose();
  };

  const handleBackToBooks = () => {
    setView('book');
  };

  const oldTestamentBooks = books?.filter(book => book?.testament === 'old') || [];
  const newTestamentBooks = books?.filter(book => book?.testament === 'new') || [];
  
  const currentBookData = books?.find(b => b?.name === currentBook);
  const chapterCount = currentBookData?.chapter_count || 0;
  const chapters = Array.from({ length: chapterCount }, (_, i) => i + 1);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Header — matches AddVerseModal style */}
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-border px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 bg-primary/15 rounded-full flex items-center justify-center">
                  <Icon name="BookOpen" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground">
                    {view === 'book' ? 'Select Book' : `${currentBook} — Select Chapter`}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {view === 'book' ? 'Choose a book of the Bible' : 'Choose a chapter'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {view === 'book' ? (
                <>
                  {/* Testament Tabs */}
                  <div className="flex gap-1 mb-6 border-b border-border">
                    <button
                      onClick={() => setActiveTestament('old')}
                      className={`px-5 py-2.5 text-sm font-medium transition-colors rounded-t-lg ${
                        activeTestament === 'old' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      Old Testament ({oldTestamentBooks?.length})
                    </button>
                    <button
                      onClick={() => setActiveTestament('new')}
                      className={`px-5 py-2.5 text-sm font-medium transition-colors rounded-t-lg ${
                        activeTestament === 'new' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      New Testament ({newTestamentBooks?.length})
                    </button>
                  </div>

                  {/* Books Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {(activeTestament === 'old' ? oldTestamentBooks : newTestamentBooks)?.map((book) => (
                      <button
                        key={book?.id}
                        onClick={() => handleBookSelect(book?.name)}
                        className={`p-3 rounded-xl border-2 transition-all text-left group ${
                          book?.name === selectedBook
                            ? 'border-primary bg-primary/10 text-primary' :'border-border bg-card hover:border-primary/50 hover:bg-primary/5 text-foreground'
                        }`}
                      >
                        <div className="font-medium text-sm leading-tight">
                          {book?.name}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {/* Back to Books */}
                  <div className="mb-5">
                    <button
                      onClick={handleBackToBooks}
                      className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name="ChevronLeft" size={16} />
                      Back to Books
                    </button>
                  </div>

                  {/* Chapters Grid */}
                  <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                    {chapters?.map((chapterNum) => (
                      <button
                        key={chapterNum}
                        onClick={() => handleChapterSelect(chapterNum)}
                        className={`aspect-square flex items-center justify-center rounded-xl border-2 transition-all font-semibold text-sm ${
                          chapterNum === selectedChapter && currentBook === selectedBook
                            ? 'border-primary bg-primary text-primary-foreground shadow-md'
                            : 'border-border bg-card hover:border-primary/50 hover:bg-primary/5 text-foreground'
                        }`}
                      >
                        {chapterNum}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BookChapterModal;